import React, { useEffect } from 'react';
import {
  Controller,
  type Control,
  type ControllerRenderProps,
  type FieldValues,
  type Path,
} from 'react-hook-form';
import {
  Field,
  FieldDescription,
  FieldError,
  FieldLabel,
} from '@/components/ui';
import { useAcademySports } from '@/queries/academySports';
import { getAcademySportLabel } from '@/utils/sportPresentation';
import { cn } from '@/lib/utils';

export interface AcademySportSelectFieldProps<T extends FieldValues> {
  academyId?: string | null;
  control: Control<T>;
  name: Path<T>;
  label?: string;
  description?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
}

function SingleSportAutoValue<T extends FieldValues>({
  field,
  sportId,
}: {
  field: ControllerRenderProps<T, Path<T>>;
  sportId: string;
}) {
  useEffect(() => {
    if (field.value !== sportId) {
      field.onChange(sportId);
    }
  }, [field, sportId]);

  return null;
}

export function AcademySportSelectField<T extends FieldValues>({
  academyId,
  control,
  name,
  label = 'Sport',
  description,
  required = false,
  disabled = false,
  className,
}: AcademySportSelectFieldProps<T>) {
  const { sports, isLoading } = useAcademySports(academyId, { enabled: !!academyId });
  const activeSports = sports.filter((s) => s.isActive);
  const showSportPicker = isLoading || activeSports.length > 1;

  if (!showSportPicker && activeSports.length === 1) {
    return (
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <SingleSportAutoValue field={field} sportId={activeSports[0]!.id} />
        )}
      />
    );
  }

  if (!showSportPicker) {
    return null;
  }

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => (
        <Field data-invalid={!!fieldState.error?.message} className={className}>
          <FieldLabel htmlFor={String(name)}>
            {label}
            {required ? ' *' : ''}
          </FieldLabel>
          {description ? <FieldDescription>{description}</FieldDescription> : null}
          <select
            id={String(name)}
            className={cn('input w-full', disabled && 'opacity-60')}
            disabled={disabled || isLoading || activeSports.length === 0}
            value={field.value ?? ''}
            onChange={(e) => field.onChange(e.target.value || undefined)}
            aria-invalid={!!fieldState.error?.message}
          >
            {!required ? <option value="">—</option> : null}
            {activeSports.map((sport) => (
              <option key={sport.id} value={sport.id}>
                {getAcademySportLabel(sport)}
                {sport.isPrimary ? ' (primary)' : ''}
              </option>
            ))}
          </select>
          {isLoading ? (
            <p className="text-xs text-muted-foreground mt-1">Loading sports…</p>
          ) : activeSports.length === 0 ? (
            <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
              Add sports in Academy Settings → Sports before assigning lessons or programs.
            </p>
          ) : null}
          <FieldError>{fieldState.error?.message}</FieldError>
        </Field>
      )}
    />
  );
}
